/**
 * Worker Hooks
 * Manages withdrawal processing for the worker panel
 *
 * Flow:
 * 1. Fetch pending withdrawals (auto-refresh every 10s)
 * 2. Lock withdrawal for processing
 * 3. Process via auto-cascade OR manual completion
 * 4. If cascade returns pending → wait for provider confirmation → confirmComplete
 */

import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useApi, useMutation } from './useApi';
import { workerAPI, mediaAPI, getCurrentUserId } from '@/services/api';
import type {
  PendingWithdrawalsResponse,
  WorkerWithdrawal,
  WithdrawalDetailResponse,
  LockWithdrawalResponse,
  AutoCascadeResponse,
  ManualCompleteResponse,
  DeclineWithdrawalResponse,
  EditAmountResponse,
  ConfirmCompleteResponse,
} from '@/types';

// =============================================================================
// Pending Withdrawals Hook
// =============================================================================

export interface UsePendingWithdrawalsOptions {
  fetchOnMount?: boolean;
  pollInterval?: number; // ms, default 10000 (10s)
  onSuccess?: (data: PendingWithdrawalsResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching and polling pending withdrawals
 */
export function usePendingWithdrawals(options: UsePendingWithdrawalsOptions = {}) {
  const { fetchOnMount = true, pollInterval = 10000, onSuccess, onError } = options;

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const currentUserId = useMemo(() => getCurrentUserId(), []);

  const api = useApi<PendingWithdrawalsResponse>(
    () => workerAPI.getPendingWithdrawals(),
    { fetchOnMount, onSuccess, onError }
  );

  // Start polling
  useEffect(() => {
    if (!fetchOnMount) return;

    intervalRef.current = setInterval(() => {
      api.execute();
    }, pollInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchOnMount, pollInterval]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper to check if current worker owns a lock
  const isOwnLock = useCallback(
    (lockedBy: string | null) => lockedBy === currentUserId,
    [currentUserId]
  );

  return {
    ...api,
    withdrawals: api.data?.withdrawals ?? [],
    total: api.data?.total ?? 0,
    refetch: api.execute,
    currentUserId,
    isOwnLock,
  };
}

// =============================================================================
// Withdrawal Detail Hook
// =============================================================================

export interface UseWithdrawalDetailOptions {
  transactionId: string | null;
  fetchOnMount?: boolean;
  onSuccess?: (data: WithdrawalDetailResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for fetching single withdrawal details
 */
export function useWithdrawalDetail(options: UseWithdrawalDetailOptions) {
  const { transactionId, fetchOnMount = true, onSuccess, onError } = options;

  const api = useApi<WithdrawalDetailResponse, [string]>(
    (id) => workerAPI.getWithdrawalDetails(id),
    { onSuccess, onError }
  );

  useEffect(() => {
    if (fetchOnMount && transactionId) {
      api.execute(transactionId);
    }
  }, [fetchOnMount, transactionId]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    ...api,
    transaction: api.data?.transaction ?? null,
    isLocked: api.data?.isLocked ?? false,
    lockedBy: api.data?.lockedBy ?? null,
    refetch: () => transactionId && api.execute(transactionId),
  };
}

// =============================================================================
// Worker Actions Hook
// =============================================================================

export interface UseWorkerActionsOptions {
  onLockSuccess?: (data: LockWithdrawalResponse) => void;
  onUnlockSuccess?: () => void;
  onCascadeSuccess?: (data: AutoCascadeResponse) => void;
  onManualSuccess?: (data: ManualCompleteResponse) => void;
  onDeclineSuccess?: (data: DeclineWithdrawalResponse) => void;
  onEditSuccess?: (data: EditAmountResponse) => void;
  onConfirmSuccess?: (data: ConfirmCompleteResponse) => void;
  onError?: (error: string) => void;
}

/**
 * Hook for all worker withdrawal actions
 */
export function useWorkerActions(options: UseWorkerActionsOptions = {}) {
  const {
    onLockSuccess,
    onUnlockSuccess,
    onCascadeSuccess,
    onManualSuccess,
    onDeclineSuccess,
    onEditSuccess,
    onConfirmSuccess,
    onError,
  } = options;

  // Lock
  const lockMutation = useMutation<LockWithdrawalResponse, [string]>(
    (id) => workerAPI.lockWithdrawal(id),
    { onSuccess: onLockSuccess, onError }
  );

  // Unlock
  const unlockMutation = useMutation<{ message: string }, [string]>(
    (id) => workerAPI.unlockWithdrawal(id),
    { onSuccess: onUnlockSuccess, onError }
  );

  // Auto-cascade
  const cascadeMutation = useMutation<AutoCascadeResponse, [string]>(
    (id) => workerAPI.autoCascade(id),
    { onSuccess: onCascadeSuccess, onError }
  );

  // Manual complete
  const manualMutation = useMutation<ManualCompleteResponse, [string, { photoFileId?: string; amountSent?: number }]>(
    (id, data) => workerAPI.manualComplete(id, data),
    { onSuccess: onManualSuccess, onError }
  );

  // Decline
  const declineMutation = useMutation<DeclineWithdrawalResponse, [string, string | undefined]>(
    (id, reason) => workerAPI.declineWithdrawal(id, { reason }),
    { onSuccess: onDeclineSuccess, onError }
  );

  // Edit amount
  const editMutation = useMutation<EditAmountResponse, [string, number]>(
    (id, newAmount) => workerAPI.editAmount(id, { newAmount }),
    { onSuccess: onEditSuccess, onError }
  );

  // Confirm complete
  const confirmMutation = useMutation<ConfirmCompleteResponse, [string]>(
    (id) => workerAPI.confirmComplete(id),
    { onSuccess: onConfirmSuccess, onError }
  );

  // Upload photo and complete manually
  const uploadAndComplete = useCallback(
    async (transactionId: string, file: File, amountSent?: number) => {
      try {
        const uploadResult = await mediaAPI.uploadPhoto(file, 'payout', transactionId);
        return await manualMutation.mutate(transactionId, {
          photoFileId: uploadResult.fileId,
          amountSent,
        });
      } catch (err) {
        onError?.((err as Error).message);
        return null;
      }
    },
    [manualMutation, onError]
  );

  return {
    // Lock
    lock: lockMutation.mutate,
    isLocking: lockMutation.isLoading,
    lockError: lockMutation.error,

    // Unlock
    unlock: unlockMutation.mutate,
    isUnlocking: unlockMutation.isLoading,
    unlockError: unlockMutation.error,

    // Auto-cascade
    autoCascade: cascadeMutation.mutate,
    isCascading: cascadeMutation.isLoading,
    cascadeResult: cascadeMutation.data,
    cascadeError: cascadeMutation.error,

    // Manual complete
    manualComplete: manualMutation.mutate,
    uploadAndComplete,
    isCompletingManually: manualMutation.isLoading,
    manualError: manualMutation.error,

    // Decline
    decline: declineMutation.mutate,
    isDeclining: declineMutation.isLoading,
    declineError: declineMutation.error,

    // Edit amount
    editAmount: editMutation.mutate,
    isEditing: editMutation.isLoading,
    editError: editMutation.error,

    // Confirm complete
    confirmComplete: confirmMutation.mutate,
    isConfirming: confirmMutation.isLoading,
    confirmError: confirmMutation.error,

    // Reset all
    resetAll: useCallback(() => {
      lockMutation.reset();
      unlockMutation.reset();
      cascadeMutation.reset();
      manualMutation.reset();
      declineMutation.reset();
      editMutation.reset();
      confirmMutation.reset();
    }, [lockMutation, unlockMutation, cascadeMutation, manualMutation, declineMutation, editMutation, confirmMutation]),
  };
}

// =============================================================================
// Combined Worker Hook
// =============================================================================

/**
 * All-in-one worker hook
 */
export function useWorker() {
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<WorkerWithdrawal | null>(null);

  const pendingWithdrawals = usePendingWithdrawals();
  const actions = useWorkerActions({
    onLockSuccess: () => pendingWithdrawals.refetch(),
    onUnlockSuccess: () => pendingWithdrawals.refetch(),
    onCascadeSuccess: () => pendingWithdrawals.refetch(),
    onManualSuccess: () => {
      pendingWithdrawals.refetch();
      setSelectedWithdrawal(null);
    },
    onDeclineSuccess: () => {
      pendingWithdrawals.refetch();
      setSelectedWithdrawal(null);
    },
    onConfirmSuccess: () => {
      pendingWithdrawals.refetch();
      setSelectedWithdrawal(null);
    },
  });

  // Process a withdrawal (lock → auto-cascade)
  const processWithdrawal = useCallback(
    async (withdrawal: WorkerWithdrawal) => {
      setSelectedWithdrawal(withdrawal);

      // Lock first
      const lockResult = await actions.lock(withdrawal.referenceNumber);
      if (!lockResult) return null;

      // Then auto-cascade
      return actions.autoCascade(withdrawal.referenceNumber);
    },
    [actions]
  );

  return {
    // Withdrawals list
    withdrawals: pendingWithdrawals.withdrawals,
    total: pendingWithdrawals.total,
    isLoading: pendingWithdrawals.isLoading,
    error: pendingWithdrawals.error,
    refetch: pendingWithdrawals.refetch,
    currentUserId: pendingWithdrawals.currentUserId,
    isOwnLock: pendingWithdrawals.isOwnLock,

    // Selected withdrawal
    selectedWithdrawal,
    setSelectedWithdrawal,

    // Actions
    lock: actions.lock,
    unlock: actions.unlock,
    autoCascade: actions.autoCascade,
    manualComplete: actions.manualComplete,
    uploadAndComplete: actions.uploadAndComplete,
    decline: actions.decline,
    editAmount: actions.editAmount,
    confirmComplete: actions.confirmComplete,
    processWithdrawal,

    // Loading states
    isLocking: actions.isLocking,
    isUnlocking: actions.isUnlocking,
    isCascading: actions.isCascading,
    isCompletingManually: actions.isCompletingManually,
    isDeclining: actions.isDeclining,
    isEditing: actions.isEditing,
    isConfirming: actions.isConfirming,

    // Results
    cascadeResult: actions.cascadeResult,

    // Reset
    resetActions: actions.resetAll,
  };
}
